'use strict';

var c = require('memory-cache');
var times = require('./times');

/**
 * Utility function to replace lodash's uniqBy
 * @template T
 * @param {Array<T>} arr - The array to process
 * @param {string} key - The key to use for uniqueness
 * @returns {Array<T>} - The array with duplicates removed
 */
function uniqBy(arr, key) {
  if (!arr) return arr;
  return [...new Map(arr.map(item => [item[key], item])).values()];
}

var cacheTTL = 5000;

function init (profileData, ctx) {

var moment = ctx.moment;

  var cache = new c.Cache();
  var profile = {};
  // The temp basal tempBasalTreatment() found last. It belongs to this instance and to the
  // current treatment list: updateTreatments() resets it along with the cache, because the
  // list it came from has been replaced (BF-94).
  var prevBasalTreatment = null;

  profile.clear = function clear() {
    cache.clear();
    profile.data = null;
    prevBasalTreatment = null;
  }

  profile.clear();

  /**
   * Loads profile data
   * @param {Array<Object>} profileData - An array of profile data objects
   */
  profile.loadData = function loadData (profileData) {
    if (profileData && profileData.length) {
      profile.data = profile.convertToProfileStore(profileData);
      profile.data?.forEach(function eachProfileRecord (record) {
        // Process each profile in the store object
        if (record.store) {
          Object.values(record.store).forEach(profile.preprocessProfileOnLoad);
        }
        record.mills = new Date(record.startDate).getTime();
      });
    }
  };
  /**
   * Converts profile data to profile store format
   * @param {Array<Object>} dataArray - The array of profile data to convert
   * @returns {Array<Object>} - The array of converted profile data
   */
  profile.convertToProfileStore = function convertToProfileStore (dataArray) {
    /** @type {Array<Object>} */
    var convertedProfiles = [];
    dataArray?.forEach(function(profile) {
      if (!profile?.store || typeof profile.store !== 'object' || Array.isArray(profile.store)) {
        /** @type {{defaultProfile: string, store: {[key: string]: Object}, startDate: string, _id: string, convertedOnTheFly: boolean}} */
        var newObject = {};
        newObject.defaultProfile = 'Default';
        newObject.store = {};
        newObject.startDate = profile.startDate ? profile.startDate : '1980-01-01';
        newObject._id = profile._id;
        newObject.convertedOnTheFly = true;
        delete profile.startDate;
        delete profile._id;
        delete profile.created_at;
        newObject.store['Default'] = profile;
        convertedProfiles.push(newObject);
        console.log('Profile not updated yet. Converted profile:', newObject);
      } else {
        delete profile.convertedOnTheFly;
        convertedProfiles.push(profile);
      }
    });
    return convertedProfiles;
  };
  /**
   * Converts a time string in format HH:MM to seconds since midnight
   * @param {string} time - Time string in format HH:MM
   * @returns {number} - Seconds since midnight
   */
  profile.timeStringToSeconds = function timeStringToSeconds (time) {
    var split = time.split(':');
    return parseInt(split[0]) * 3600 + parseInt(split[1]) * 60;
  };  // preprocess the timestamps to seconds for a couple orders of magnitude faster operation
  /**
   * Preprocesses profile data to convert time strings to seconds for faster operations
   * @param {Object|null} container - The profile data container to process
   */
  profile.preprocessProfileOnLoad = function preprocessProfileOnLoad (container) {
    Object.values(container || {}).forEach(function eachValue (/** @type {any} */ value) {

      if (value === null) return;

      if (Object.prototype.toString.call(value) === '[object Array]') {
        profile.preprocessProfileOnLoad(value);
      }

      if (value.time) {
        /** @type {number} */
        var sec = profile.timeStringToSeconds(value.time);
        if (!isNaN(sec)) { value.timeAsSeconds = sec; }
      }
    });
  };

  profile.getValueByTime = function getValueByTime (time, valueType, spec_profile) {
    if (!time) { time = Date.now(); }

    //round to the minute for better caching
    var minuteTime = Math.round(time / 60000) * 60000;
    var cacheKey = (minuteTime + valueType + spec_profile);
    var returnValue = cache.get(cacheKey);

    if (returnValue) {
      return returnValue;
    }

    // CircadianPercentageProfile support
    var timeshift = 0;
    var percentage = 100;
    var activeTreatment = profile.activeProfileTreatmentToTime(time);
    var isCcpProfile = !spec_profile && activeTreatment && activeTreatment.CircadianPercentageProfile;
    if (isCcpProfile) {
      percentage = activeTreatment.percentage;
      timeshift = activeTreatment.timeshift; // in hours
    }
    var offset = timeshift % 24;
    time = time + offset * times.hours(offset).msecs;

    // AndroidAPS 3.x Profile Switch: percentage and timeshift apply to the embedded profileJson,
    // which AAPS uploads unscaled (see profile.aapsSwitchAdjustment).
    var aapsAdjustment = !spec_profile && !isCcpProfile ? profile.aapsSwitchAdjustment(activeTreatment) : null;

    var valueContainer = profile.getCurrentProfile(time, spec_profile)[valueType];

    // Assumes the timestamps are in UTC
    // Use local time zone if profile doesn't contain a time zone
    // This WILL break on the server; added warnings elsewhere that this is missing
    // TODO: Better warnings to user for missing configuration

    var t = profile.applyTimezone(moment(minuteTime), spec_profile);

    // Convert to seconds from midnight
    var mmtMidnight = t.clone().startOf('day');
    var timeAsSecondsFromMidnight = t.clone().diff(mmtMidnight, 'seconds');
    if (aapsAdjustment && aapsAdjustment.timeshiftHours) {
      // AAPS reads the schedule at (seconds from midnight - timeshift), wrapped to one day
      timeAsSecondsFromMidnight = ((timeAsSecondsFromMidnight - aapsAdjustment.timeshiftHours * 3600) % 86400 + 86400) % 86400;
    }

    // If the container is an Array, assume it's a valid timestamped value container

    returnValue = valueContainer;    if (Object.prototype.toString.call(valueContainer) === '[object Array]') {
      valueContainer?.forEach(function eachValue (value) {
        if (timeAsSecondsFromMidnight >= value.timeAsSeconds) {
          returnValue = value.value;
        }
      });
    }

    if (returnValue) {
      returnValue = parseFloat(returnValue);
      if (isCcpProfile) {
        switch (valueType) {
          case "sens":
          case "carbratio":
            returnValue = returnValue * 100 / percentage;
            break;
          case "basal":
            returnValue = returnValue * percentage / 100;
            break;
        }
      } else if (aapsAdjustment && aapsAdjustment.percentage !== 100) {
        switch (valueType) {
          case "sens":
          case "carbratio":
            returnValue = returnValue * 100 / aapsAdjustment.percentage;
            break;
          case "basal":
            returnValue = returnValue * aapsAdjustment.percentage / 100;
            break;
        }
      }
    }

    cache.put(cacheKey, returnValue, cacheTTL);

    return returnValue;
  };

  profile.getCurrentProfile = function getCurrentProfile (time, spec_profile) {

    time = time || Date.now();
    var minuteTime = Math.round(time / 60000) * 60000;
    var cacheKey = ("profile" + minuteTime + spec_profile);
    var returnValue = cache.get(cacheKey);

    if (returnValue) {
      return returnValue;
    }

    var pdataActive = profile.profileFromTime(time);
    var data = profile.hasData() ? pdataActive : null;
    var timeprofile = profile.activeProfileToTime(time);
    returnValue = data && timeprofile !== null && Object.prototype.hasOwnProperty.call(data.store, timeprofile)
      ? data.store[timeprofile] : {};

    cache.put(cacheKey, returnValue, cacheTTL);
    return returnValue;

  };

  profile.getUnits = function getUnits (spec_profile) {
    var pu = profile.getCurrentProfile(null, spec_profile)['units'] + ' ';
    if (pu.toLowerCase().includes('mmol')) return 'mmol';
    return 'mg/dl';
  };

  function isFixedOffset (tz) {
    return /^[+-]\d{2}:\d{2}$/.test(tz);
  }

  profile.getTimezone = function getTimezone (spec_profile) {
    var rVal = profile.getCurrentProfile(null, spec_profile)['timezone'];
    if (rVal) {
      // Work around Loop uploading non-ISO compliant time zone string
      rVal = rVal.replace('ETC', 'Etc');
      // Normalize non-IANA offset timezones uploaded by Loop / Trio.
      // Whole-hour offsets map to Etc/GMT (IANA inverts the sign: GMT+4 = Etc/GMT-4).
      // Sub-hour offsets (GMT+5:30, GMT-3:30, GMT+5:45) become fixed-offset strings
      // like +05:30 — Etc/GMT has no sub-hour zones, so call sites detect and apply
      // these via utcOffset / parseZone instead of moment.tz().
      var match = rVal.match(/^(?:GMT|UTC)([+-])(\d{1,2})(?::(\d{2}))?$/i);
      if (match) {
        var minutes = match[3] ? parseInt(match[3], 10) : 0;
        if (minutes === 0) {
          var sign = match[1] === '+' ? '-' : '+';
          rVal = 'Etc/GMT' + sign + match[2];
        } else if (minutes < 60) {
          var hh = match[2].length === 1 ? '0' + match[2] : match[2];
          var mm = match[3].length === 1 ? '0' + match[3] : match[3];
          rVal = match[1] + hh + ':' + mm;
        }
        // minutes >= 60 → invalid, leave rVal unchanged for downstream UTC fallback
      }
    }
    return rVal;
  };

  // Apply the profile's timezone to an existing moment object.
  // Handles both IANA names (Asia/Tokyo) and fixed-offset strings (+05:30).
  // Use this instead of `moment(t).tz(profile.getTimezone())` at call sites.
  profile.applyTimezone = function applyTimezone (mom, spec_profile) {
    var tz = profile.getTimezone(spec_profile);
    if (!tz) return mom;
    if (isFixedOffset(tz)) return mom.utcOffset(tz);
    return mom.tz(tz);
  };

  // Parse a date/time string in the profile's timezone.
  // Use this instead of `moment.tz(str, profile.getTimezone())` at call sites.
  profile.parseInTimezone = function parseInTimezone (str, spec_profile) {
    var tz = profile.getTimezone(spec_profile);
    if (!tz) return moment(str);
    if (isFixedOffset(tz)) {
      var sep = /[T ]/.test(str) ? '' : 'T00:00:00';
      return moment.parseZone(str + sep + tz);
    }
    return moment.tz(str, tz);
  };

  profile.hasData = function hasData () {
    return profile.data ? true : false;
  };

  profile.getDIA = function getDIA (time, spec_profile) {
    return profile.getValueByTime(Number(time), 'dia', spec_profile);
  };

  profile.getSensitivity = function getSensitivity (time, spec_profile) {
    return profile.getValueByTime(Number(time), 'sens', spec_profile);
  };

  profile.getCarbRatio = function getCarbRatio (time, spec_profile) {
    return profile.getValueByTime(Number(time), 'carbratio', spec_profile);
  };

  profile.getCarbAbsorptionRate = function getCarbAbsorptionRate (time, spec_profile) {
    return profile.getValueByTime(Number(time), 'carbs_hr', spec_profile);
  };

  profile.getLowBGTarget = function getLowBGTarget (time, spec_profile) {
    return profile.getValueByTime(Number(time), 'target_low', spec_profile);
  };

  profile.getHighBGTarget = function getHighBGTarget (time, spec_profile) {
    return profile.getValueByTime(Number(time), 'target_high', spec_profile);
  };

  profile.getBasal = function getBasal (time, spec_profile) {
    return profile.getValueByTime(Number(time), 'basal', spec_profile);
  };

  profile.updateTreatments = function updateTreatments (profiletreatments, tempbasaltreatments, combobolustreatments) {

    prevBasalTreatment = null;

    profile.profiletreatments = profiletreatments || [];
    profile.tempbasaltreatments = tempbasaltreatments || [];

    // dedupe temp basal events
    profile.tempbasaltreatments = uniqBy(profile.tempbasaltreatments, 'mills');

    profile.tempbasaltreatments?.forEach(function addDuration (t) {
      t.endmills = t.mills + times.mins(t.duration || 0).msecs;
    });

    profile.tempbasaltreatments.sort(function compareTreatmentMills (a, b) {
      return a.mills - b.mills;
    });

    profile.combobolustreatments = combobolustreatments || [];

    cache.clear();
  };

  profile.getBasalRenderTimes = function getBasalRenderTimes (from, to, stepMsecs) {
    from = Number(from);
    to = Number(to);
    stepMsecs = Number(stepMsecs) || times.min().msecs;

    var renderTimes = {};

    function addTime (time) {
      time = Number(time);
      if (Number.isFinite(time) && time >= from && time <= to) {
        renderTimes[time] = true;
      }
    }

    function treatmentEndMills (treatment) {
      if (Number.isFinite(Number(treatment.endmills))) {
        return Number(treatment.endmills);
      }
      return Number(treatment.mills) + times.mins(Number(treatment.duration) || 0).msecs;
    }

    function addTreatmentBoundaries (treatment) {
      var start = Number(treatment.mills);
      var end = treatmentEndMills(treatment);

      if (!Number.isFinite(start)) {
        return;
      }

      addTime(start);
      if (Number.isFinite(end)) {
        addTime(end + 1);
      }
    }

    addTime(from);
    addTime(to);

    for (var time = from; time <= to; time += stepMsecs) {
      addTime(time);
    }

    (profile.profiletreatments || []).forEach(addTreatmentBoundaries);
    (profile.tempbasaltreatments || []).forEach(addTreatmentBoundaries);
    (profile.combobolustreatments || []).forEach(addTreatmentBoundaries);

    return Object.keys(renderTimes).map(Number).sort(function sortNumbers (a, b) {
      return a - b;
    });
  };

  profile.activeProfileToTime = function activeProfileToTime (time) {
    if (profile.hasData()) {
      time = Number(time) || new Date().getTime();

      var pdataActive = profile.profileFromTime(time);
      var timeprofile = pdataActive.defaultProfile;
      var treatment = profile.activeProfileTreatmentToTime(time);

      if (treatment && typeof treatment.profile === 'string' && pdataActive.store
          && Object.prototype.hasOwnProperty.call(pdataActive.store, treatment.profile)) {
        timeprofile = treatment.profile;
      }
      return typeof timeprofile === 'string' && pdataActive.store
        && Object.prototype.hasOwnProperty.call(pdataActive.store, timeprofile) ? timeprofile : null;
    }
    return null;
  };

  profile.activeProfileTreatmentToTime = function activeProfileTreatmentToTime (time) {

    var minuteTime = Math.round(time / 60000) * 60000;
    var cacheKey = 'profileCache' + minuteTime;
    var returnValue = cache.get(cacheKey);

    if (returnValue) {
      return returnValue;
    }

    var treatment = null;
    if (profile.hasData()) {
      var pdataActive = profile.profileFromTime(time);
        profile.profiletreatments.forEach(function eachTreatment(t) {
          if (time >= t.mills && t.mills >= pdataActive.mills) {
              var duration = times.mins(t.duration || 0).msecs;
              if (duration != 0 && time < t.mills + duration) {
                  treatment = t;
                  // if profile switch contains json of profile inject it in to store to be findable by profile name
                  if (treatment.profileJson && !pdataActive.store[treatment.profile]) {
                    if (treatment.profile.indexOf("@@@@@") < 0)
                      treatment.profile += "@@@@@" + treatment.mills;
                    let json = JSON.parse(treatment.profileJson);
                    profile.preprocessProfileOnLoad(json);
                    pdataActive.store[treatment.profile] = json;
                  }
              }
              if (duration == 0) {
                treatment = t;
                // if profile switch contains json of profile inject it in to store to be findable by profile name
                if (treatment.profileJson && !pdataActive.store[treatment.profile]) {
                    if (treatment.profile.indexOf("@@@@@") < 0)
                      treatment.profile += "@@@@@" + treatment.mills;
                  let json = JSON.parse(treatment.profileJson);
                  profile.preprocessProfileOnLoad(json);
                  pdataActive.store[treatment.profile] = json;
                }
              }
          }
      });
    }

    returnValue = treatment;
    cache.put(cacheKey, returnValue, cacheTTL);
    return returnValue;
  };

  /**
   * The percentage and timeshift of an AndroidAPS 3.x Profile Switch, or null when the treatment
   * does not have that shape.
   *
   * AAPS 3.x uploads a Profile Switch with a numeric `percentage`, a `timeshift` in milliseconds,
   * and a `profileJson` it serialises with percentage 100 and timeshift 0, so the embedded
   * profile is unscaled (AndroidAPS nsclientV3/extensions/ProfileSwitchExtension.kt
   * toNSProfileSwitch). AAPS then reads basal x percentage/100, ISF and carb ratio x 100/percentage,
   * targets unscaled, and every schedule at (time - timeshift), with the timeshift truncated to
   * whole hours (core/objects ProfileSealed.kt, extensions/BlockExtension.kt).
   *
   * Switches flagged CircadianPercentageProfile (AAPS 2.x, timeshift in hours) keep their own path.
   * A switch without an embedded profileJson, or without a numeric percentage, is not this shape.
   * @param {Object|null} treatment - the active Profile Switch treatment
   * @returns {{percentage: number, timeshiftHours: number}|null}
   */
  profile.aapsSwitchAdjustment = function aapsSwitchAdjustment (treatment) {
    if (!treatment || treatment.CircadianPercentageProfile || !treatment.profileJson) return null;
    var percentage = treatment.percentage;
    if (typeof percentage !== 'number' || !Number.isFinite(percentage) || percentage <= 0) return null;
    var timeshiftHours = 0;
    if (typeof treatment.timeshift === 'number' && Number.isFinite(treatment.timeshift)) {
      timeshiftHours = Math.trunc(treatment.timeshift / times.hour().msecs) % 24;
    }
    if (percentage === 100 && timeshiftHours === 0) return null;
    return { percentage: percentage, timeshiftHours: timeshiftHours };
  };

  profile.profileSwitchName = function profileSwitchName (name) {
    var index = name.indexOf("@@@@@");
    if (index < 0) return name;
    else return name.substring(0, index);
  }

  profile.profileFromTime = function profileFromTime (time) {
      var profileData = null;

      if (profile.hasData()) {
          profileData = profile.data[0];
          for (var i = 0; i < profile.data.length; i++)
          {
              if (Number(time) >= Number(profile.data[i].mills)) {
                  profileData = profile.data[i];
                  break;
              }
          }
      }

      return profileData;
  }

  profile.tempBasalTreatment = function tempBasalTreatment (time) {

    // Most queries for the data in reporting will match the latest found value, caching that hugely improves performance
    if (prevBasalTreatment && time >= prevBasalTreatment.mills && time <= prevBasalTreatment.endmills) {
      return prevBasalTreatment;
    }

    // Binary search for events for O(log n) performance
    var first = 0
      , last = profile.tempbasaltreatments.length - 1;

    while (first <= last) {
      var i = first + Math.floor((last - first) / 2);
      var t = profile.tempbasaltreatments[i];
      if (time >= t.mills && time <= t.endmills) {
        prevBasalTreatment = t;
        return t;
      }
      if (time < t.mills) {
        last = i - 1;
      } else {
        first = i + 1;
      }
    }

    return null;
  };

  profile.comboBolusTreatment = function comboBolusTreatment (time) {
    var treatment = null;
    profile.combobolustreatments.forEach(function eachTreatment (t) {
      var duration = times.mins(t.duration || 0).msecs;
      if (time < t.mills + duration && time > t.mills) {
        treatment = t;
      }
    });
    return treatment;
  };

  profile.getTempBasal = function getTempBasal (time, spec_profile) {

    time = Number(time);
    var cacheKey = 'basalCache' + time + spec_profile;
    var returnValue = cache.get(cacheKey);

    if (returnValue) {
      return returnValue;
    }

    var basal = profile.getBasal(time, spec_profile);
    var tempbasal = basal;
    var combobolusbasal = 0;
    var treatment = profile.tempBasalTreatment(time);
    var combobolustreatment = profile.comboBolusTreatment(time);

    //special handling for absolute to support temp to 0
    if (treatment && !isNaN(treatment.absolute) && treatment.duration > 0) {
      tempbasal = Number(treatment.absolute);
    } else if (treatment && treatment.percent) {
      tempbasal = basal * (100 + treatment.percent) / 100;
    }
    if (combobolustreatment && combobolustreatment.relative) {
      combobolusbasal = combobolustreatment.relative;
    }
    returnValue = {
      basal: basal
      , treatment: treatment
      , combobolustreatment: combobolustreatment
      , tempbasal: tempbasal
      , combobolusbasal: combobolusbasal
      , totalbasal: tempbasal + combobolusbasal
    };
    cache.put(cacheKey, returnValue, cacheTTL);
    return returnValue;
  };

  profile.listBasalProfiles = function listBasalProfiles () {
    var profiles = [];
    if (profile.hasData()) {
      var current = profile.activeProfileToTime();
      profiles.push(current);

      Object.keys(profile.data[0].store).forEach(key => {
        if (key !== current && key.indexOf('@@@@@') < 0) profiles.push(key);
      })
    }
    return profiles;
  };

  if (profileData) { profile.loadData(profileData); }
  // init treatments array
  profile.updateTreatments([], []);

  return profile;
}

module.exports = init;
